import { FAMILY_ID, type FamilyId } from '../matrix/familyRecipes';
import type { LocalBusinessSocialLinks } from './studio';
import type { SiteSocialNetworkKey } from './socialLinks';

/**
 * Demo / bootstrap social networks by service family (DE gastronomy 2024–26).
 *
 * Supported store keys only: facebook · instagram · tiktok · x · youtube.
 * WhatsApp is a messenger CTA, not a Local Presence social profile.
 * X is rarely used for DE guest discovery — omitted from seeds.
 *
 * Sources (directional): GroupM Social Platform Profiler DE, YouGov DE Social
 * Media Report, Bitkom dining discovery, GastroInsider channel-fit by concept.
 */
export const FAMILY_DEFAULT_SOCIAL_NETWORKS = {
  /** Editorial plate photography + Meta events / 40+ guests. */
  [FAMILY_ID.fineDining]: ['instagram', 'facebook'],
  /** Neighborhood discovery; TikTok for younger bistro kitchens. */
  [FAMILY_ID.bistro]: ['instagram', 'facebook', 'tiktok'],
  /** Family/local Facebook + Instagram card; TikTok for Gen Z bowls/pho. */
  [FAMILY_ID.asian]: ['instagram', 'facebook', 'tiktok'],
  /** Aesthetic feed + short-form coffee / pastry discovery. */
  [FAMILY_ID.cafe]: ['instagram', 'tiktok'],
  /** Nightlife / cocktail short-form; Instagram as venue card. */
  [FAMILY_ID.bar]: ['instagram', 'tiktok'],
  /** Street discovery (TikTok/IG) + local Facebook reach. */
  [FAMILY_ID.imbiss]: ['instagram', 'tiktok', 'facebook'],
  /** Location updates + food video; Facebook events optional later. */
  [FAMILY_ID.foodTruck]: ['instagram', 'tiktok'],
} as const satisfies Record<FamilyId, readonly SiteSocialNetworkKey[]>;

export type FamilyDefaultSocialNetwork =
  (typeof FAMILY_DEFAULT_SOCIAL_NETWORKS)[FamilyId][number];

/** Profile URL for a curated network + demo handle (no leading @). */
export function demoSocialProfileUrl(
  network: SiteSocialNetworkKey,
  handle: string,
): string {
  const slug = handle.replace(/^@+/, '').trim();
  switch (network) {
    case 'facebook':
      return `https://www.facebook.com/${slug}`;
    case 'instagram':
      return `https://www.instagram.com/${slug}`;
    case 'tiktok':
      return `https://www.tiktok.com/@${slug}`;
    case 'x':
      return `https://x.com/${slug}`;
    case 'youtube':
      return `https://www.youtube.com/@${slug}`;
  }
}

/** Build Local Presence `socialLinks` for a family's default networks. */
export function buildFamilyDefaultSocialLinks(
  familyId: FamilyId,
  handle: string,
): LocalBusinessSocialLinks {
  const links: LocalBusinessSocialLinks = {};
  for (const network of FAMILY_DEFAULT_SOCIAL_NETWORKS[familyId]) {
    links[network] = demoSocialProfileUrl(network, handle);
  }
  return links;
}
